import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, CardContent, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedGoal } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Goal';
import { usePlanDefinitions } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';
import useObjectState from 'src/hooks/useObjectState';
import SeverityStatus, { AlertSeverity } from 'src/components/SeverityStatus';
import { GoalGridRowData } from '../../../../../@types/crs/case';
import { getGoalGridRows, getParentGoalGridRows } from 'src/careflow/helpers/common';
import './styles.css';
import {
  PlanDefinitionWrapper,
  WrappedPlanDefinition,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PlanDefinition';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import GoalSuggestionsGrid, { GoalSuggestion } from '../suggestion/GoalSuggestionsGrid';
import { GoalsPermissions } from 'src/careflow/helpers/types';
import { PermissionsProvider } from 'src/careflow/ph-core/admin/permissions/context/PermissionsContext';
import { CollapsibleProvider } from 'src/contexts/CollapsibleContext';
import GoalsGridWithFilters from '../grid/GoalsGridWithFilters';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';

interface GoalsByPathwayProps {
  goals: WrappedGoal[];
  tasks: WrappedTask[];
  patient: WrappedPatient | null;
  carePlan: WrappedCarePlan | null;
  planDefinitions: WrappedPlanDefinition[];
  conditions: WrappedCondition[];
  observations: WrappedObservation[];
  openCollapseExternal: boolean;
  permissions: GoalsPermissions;
  title?: string;
  isCollapsible?: boolean;
}
interface GoalsByPathwayState {
  selectedPathway: string;
}

const pathwayAll: ValueSetComposeIncludeConcept = {
  code: 'all',
  display: 'All',
};

const GoalsByPathway = React.memo(
  ({
    patient,
    carePlan,
    goals,
    tasks,
    planDefinitions,
    conditions,
    observations,
    openCollapseExternal,
    permissions,
    title,
    isCollapsible = true
  }: GoalsByPathwayProps) => {
    const [{ selectedPathway }, updateState] = useObjectState<GoalsByPathwayState>({
      selectedPathway: 'all',
    });

    const { i18n } = useLocales();

    const { componentsData } = useTenantConfigData();
    const { showSubGoals, goal } = componentsData ?? {};

    const {
      valueSets: [pathways, goalStatuses],
      isLoading: isPathWaysLoading,
    } = useValueSetsByIdentifiers(['ph-path-ways', 'ph-goal-lifecycle-status']);

    const [plandefinitionFilterTags, setPlandefinitionFilterTags] = useState<string[]>([]);
    const [goalsSuggestions, setGoalSuggestions] = useState<GoalSuggestion[]>([]);

    const [planDefinitionSuggestions] = usePlanDefinitions({
      map: PlanDefinitionWrapper,
      filter: {
        _tag: plandefinitionFilterTags.join(','),
      },
      autofetch: !!plandefinitionFilterTags.length,
    });

    const getGoalsSuggestions = useCallback((): GoalSuggestion[] => {
      if (!planDefinitionSuggestions || !planDefinitionSuggestions.length) return [];
      const usedPlanDefinitions =
        carePlan?.activity?.reduce?.<string[]>((planDefinitions, activity) => {
          const { detail } = activity;
          const planDefinitionsReferences =
            detail?.instantiatesCanonical?.filter?.(
              (reference) => reference?.split?.('/')?.[0] === 'PlanDefinition'
            ) ?? [];
          if (!planDefinitionsReferences.length) return planDefinitions;
          return [
            ...planDefinitions,
            ...planDefinitionsReferences.map((reference) => reference.split('/')[1]),
          ];
        }, []) ?? [];

      const goalSuggestions: GoalSuggestion[] = [];

      conditions.map((condition) => {
        const { code, system } = condition?.code?.coding?.[0] ?? {};
        if (!code || !system) return;
        const matchingPlanDefinitions = planDefinitionSuggestions.filter(
          ({ goal }) =>
            !!goal?.some?.(
              ({ addresses }) =>
                !!addresses?.some?.(
                  ({ coding }) =>
                    !!coding?.find?.((coding) => coding?.code === code && coding.system === system)
                )
            )
        );
        if (!matchingPlanDefinitions.length) return;
        matchingPlanDefinitions.map((matchingPlanDefinition) => {
          if (!matchingPlanDefinition.id) return;
          if (usedPlanDefinitions.includes(matchingPlanDefinition.id)) return;
          const goalSuggestion = goalSuggestions.find(
            ({ planDefinition }) => planDefinition === matchingPlanDefinition
          );
          if (goalSuggestion) {
            if (
              !!goalSuggestion.addresses.find((condition) => {
                const { code: suggestionConditionCode, system: suggestionConditionSystem } =
                  condition?.code?.coding?.[0] ?? {};
                return code === suggestionConditionCode && system === suggestionConditionSystem;
              })
            )
              return;
            goalSuggestion.addresses.push(condition);
            return;
          }
          goalSuggestions.push({ addresses: [condition], planDefinition: matchingPlanDefinition });
        });
      });

      observations.map((observation) => {
        const { code, system } = observation?.valueCodeableConcept?.coding?.[0] ?? {};
        if (!code || !system) return;
        const matchingPlanDefinitions = planDefinitionSuggestions.filter(
          ({ goal }) =>
            !!goal?.some?.(
              ({ addresses }) =>
                !!addresses?.some?.(
                  ({ coding }) =>
                    !!coding?.find?.((coding) => coding?.code === code && coding.system === system)
                )
            )
        );
        if (!matchingPlanDefinitions.length) return;
        matchingPlanDefinitions.map((matchingPlanDefinition) => {
          if (!matchingPlanDefinition.id) return;
          if (usedPlanDefinitions.includes(matchingPlanDefinition.id)) return;
          const goalSuggestion = goalSuggestions.find(
            ({ planDefinition }) => planDefinition === matchingPlanDefinition
          );
          if (goalSuggestion) {
            if (
              !!goalSuggestion.addresses.find((observation) => {
                const coding = observation?.getSugestionCoding();
                const { code: suggestionConditionCode, system: suggestionConditionSystem } = coding ?? {};
                return code === suggestionConditionCode && system === suggestionConditionSystem;
              })
            )
              return;
            goalSuggestion.addresses.push(observation);
            return;
          }
          goalSuggestions.push({
            addresses: [observation],
            planDefinition: matchingPlanDefinition,
          });
        });
      });

      return goalSuggestions;
    }, [planDefinitionSuggestions, carePlan, conditions, observations]);

    useEffect(() => {
      const goalsSuggestions = getGoalsSuggestions();
      setGoalSuggestions(goalsSuggestions);
    }, [getGoalsSuggestions]);

    useEffect(() => {
      if (!conditions.length && !observations.length) return;

      let tagsToFilterBy: string[] = [];

      if (conditions.length) {
        const conditionsTagsToFilterBy = conditions.reduce<string[]>((tagFilters, condition) => {
          const { code, system } = condition?.code?.coding?.[0] ?? {};
          if (!code || !system) return tagFilters;

          const tag = `${system}|${code}`;
          if (tagFilters.includes(tag)) return tagFilters;
          return [...tagFilters, tag];
        }, []);

        tagsToFilterBy = tagsToFilterBy.concat(conditionsTagsToFilterBy);
      }

      if (observations.length) {
        const conditionsTagsToFilterBy = observations.reduce<string[]>(
          (tagFilters, observation) => {
            const { code, system } = observation?.valueCodeableConcept?.coding?.[0] ?? {};
            if (!code || !system) return tagFilters;

            const tag = `${system}|${code}`;
            if (tagFilters.includes(tag)) return tagFilters;
            return [...tagFilters, tag];
          },
          []
        );

        tagsToFilterBy = tagsToFilterBy.concat(conditionsTagsToFilterBy);
      }

      setPlandefinitionFilterTags(tagsToFilterBy);
    }, [conditions, observations]);

    // Get goals
    const groupedGoals = useMemo(() => carePlan?.getGroupedGoals(), [carePlan]);
    
    const goalGridRows = useMemo(
      () =>
        getParentGoalGridRows(
          goals,
          tasks,
          conditions,
          groupedGoals,
          goalStatuses?.asList?.() ?? [],
          observations
        ),
      [goals, tasks, groupedGoals, goalStatuses, conditions]
    );

    const handlePathwayTabChange = (_: React.SyntheticEvent, pathway: string) => {
      updateState({
        selectedPathway: pathway,
      });
    };

    const pathwaysList: ValueSetComposeIncludeConcept[] = [
      ...(goalsSuggestions && goalsSuggestions.length > 0
        ? [{ code: 'suggestions', display: 'Suggestions' }]
        : []),
      pathwayAll,
      ...(pathways?.asList?.() ?? [])
    ];

    const statusesList = useMemo(() => {
      const allGoalGridRows = showSubGoals?.includeSubGoals ? goals?.map((g) => 
        getGoalGridRows(g, [], [], [], undefined, goalStatuses?.asList?.() ?? [])
      ) : goalGridRows;
      const gridGoals = getStatusCountByPathway(allGoalGridRows, goal?.statusCountByPathway);
      const statuses = {
        ...gridGoals,
        ...(goalsSuggestions.length > 0 && {
          info: `${goalsSuggestions.length} ${
            goalsSuggestions.length > 0
              ? i18n('case.details.suggestions', 'crs')
              : i18n('case.details.suggestion', 'crs')
          }`,
        }),
      };
      if(goalsSuggestions.length > 0) 
        updateState({selectedPathway: 'suggestions'})
      else
        updateState({selectedPathway: 'all'})
      return statuses;
    }, [goals, goalGridRows, showSubGoals, goalsSuggestions]);

    return (
      <PermissionsProvider permissions={permissions}>
        {!isPathWaysLoading ? (
          <CollapsibleProvider
            title={title}
            openCollapseExternal={openCollapseExternal}
            isCollapsible={isCollapsible}
          >
            <Tabs
              scrollButtons={'auto'}
              variant={'scrollable'}
              value={selectedPathway}
              onChange={handlePathwayTabChange}
              aria-label="PathWay Tab"
            >
              {pathwaysList.map(({ code, display }) => {
                const statuses = getStatusCountByPathway(getGoalsByPathWay(code, goalGridRows), goal?.statusCountByPathway);
                const suggestions = {
                  info: goalsSuggestions.length,
                };
                const entries = code === 'suggestions' ? suggestions : statuses;
                
                return (
                  <Tab
                    key={code}
                    label={
                      <Box display={'flex'} flexDirection={'row'} sx={{ width: '100%' }}>
                        <Typography sx={{ fontSize: '1em', width: '100%', whiteSpace: 'nowrap' }}>
                          {display}
                        </Typography>
                        {Object.entries(entries).map(([severity, count]) => (
                          <SeverityStatusCount
                            key={severity}
                            count={count}
                            severity={severity as AlertSeverity}
                          />
                        ))}
                      </Box>
                    }
                    value={code}
                  />
                );
              })}
            </Tabs>
            <CardContent className={'cardContent'} sx={{ padding: 0 }}>
              <Box sx={{ marginTop: 2, paddingBottom: 0 }}>
                {pathwaysList.map((pathway) => {
                  const { code } = pathway;
                  const goalGridRowsByPathway = getGoalsByPathWay(code, goalGridRows);

                  return (
                    <TabPanel key={code} pathway={code} selectedPathway={selectedPathway}>
                      {code === 'suggestions' ? 
                        <Box sx={{ marginTop: 2 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ pl: 3, mb: 3 }}
                          >
                            {i18n('case.details.goalsSuggestions', 'crs')}
                          </Typography>
                          <GoalSuggestionsGrid
                            goalSuggestions={goalsSuggestions}
                            carePlan={carePlan}
                            planDefinitions={planDefinitions}
                            conditions={conditions}
                            observations={observations}
                            patient={patient}
                            pathways={pathways}
                          />
                        </Box>
                      : 
                        <GoalsGridWithFilters
                          includeSubGoals={showSubGoals?.includeSubGoals}
                          noOfSubGrids={showSubGoals?.noOfSubGrids}
                          showCategory={code === pathwayAll.code}
                          planDefinitions={planDefinitions}
                          conditions={conditions}
                          observations={observations}
                          patient={patient as WrappedPatient}
                          goals={goalGridRowsByPathway}
                          defaultPathway={code !== pathwayAll.code ? pathway : undefined}
                          pathways={(pathways?.asList?.() ?? []) as ValueSetComposeIncludeConcept[]}
                          showPathwayFilter={selectedPathway === pathwayAll.code}
                          carePlan={carePlan as WrappedCarePlan}
                          statusesList={statusesList}
                          title={title}
                        />
                        }
                    </TabPanel>
                  );
                })}
              </Box>
            </CardContent>
          </CollapsibleProvider>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              my: 2,
            }}
          >
            <CircularProgress size={50} />
          </Box>
        )}
      </PermissionsProvider>
    );
  }
);

interface TabPanelProps {
  children: React.ReactNode;
  selectedPathway: string;
  pathway: string;
}

const TabPanel = ({ children, selectedPathway, pathway }: TabPanelProps): JSX.Element => (
  <>{selectedPathway === pathway ? <>{children}</> : null}</>
);

type DueDateSeverities =
  | AlertSeverity.ERROR
  | AlertSeverity.WARNING
  | AlertSeverity.SUCCESS
  | AlertSeverity.DEFAULT;

export type StatusSeverityCount = Record<DueDateSeverities, number>;

type StatusByConfig = { open: string[], close: string[] };

export const getStatusCountByPathway = (
  goals: GoalGridRowData[], 
  statusByConfig?: StatusByConfig
): StatusSeverityCount => {
  const openCloseStatus = statusByConfig ? 
    statusByConfig : { 
      open: ['Proposed', 'Planned', 'Accepted', 'Active', 'On Hold'], 
      close: ['Completed', 'Cancelled', 'Rejected', 'Entered In Error'] 
    };
  // Insertion order drives badge order: red -> yellow -> green -> gray.
  const statuses: StatusSeverityCount = {
    [AlertSeverity.ERROR]: 0,
    [AlertSeverity.WARNING]: 0,
    [AlertSeverity.SUCCESS]: 0,
    [AlertSeverity.DEFAULT]: 0,
  };
  for (const {
    endDate: { severity },
    status,
  } of goals) {
    if(statuses.hasOwnProperty(severity as AlertSeverity)){
      // Goals without a target due date are counted on their own, never as
      // green/closed nor yellow "Due Today". Mirrors the task counter so the
      // two step badges stay consistent.
      if (severity as DueDateSeverities === AlertSeverity.DEFAULT){
        statuses[AlertSeverity.DEFAULT as DueDateSeverities]++;
      }
      else if (
        severity as DueDateSeverities === AlertSeverity.SUCCESS || 
        openCloseStatus.close.includes(status)
      ){
        statuses[AlertSeverity.SUCCESS as DueDateSeverities]++;}
      else if(
        severity as DueDateSeverities !== AlertSeverity.SUCCESS || 
        openCloseStatus.open.includes(status)
      ){
        statuses[severity as DueDateSeverities]++;
      }
    }
  }
  return statuses;
};

const getGoalsByPathWay = (pathwayCode: string | null, goals: GoalGridRowData[]) => {
  if (!pathwayCode || pathwayCode === pathwayAll.code) return goals;
  return goals.filter(({ pathway }) => pathway === pathwayCode);
};

interface SeverityStatusCountProps {
  count: number;
  severity: AlertSeverity;
}

const SeverityStatusCount = ({ count, severity }: SeverityStatusCountProps) => (
  <SeverityStatus status={{ message: String(count), severity }} sx={{ height: '20px', ml: 0.5 }} />
);

export default GoalsByPathway;
